import React, { ReactElement, useEffect, useState } from 'react';

import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { Tier } from '@backrs_ui/common-ui/src/models/careerPath';
import { AskLubavButtonEndpoint } from '@backrs_ui/common-ui/src/models/nugget';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';

import { Box, SxProps, Theme } from '@mui/material';

import { AskLubavButton } from './AskLubavButton';
import { AskLubavErrorCode } from './AskLubavError';
import { AskLubavResponse } from './AskLubavResponse';

export type HandleAPICallResponseReturn = Readonly<{
  response: string | undefined;
  isError: boolean;
  loadingButton: AskLubavButtonEndpoint | undefined;
  errorCode?: AskLubavErrorCode;
}>;

export type ButtonRequirements = Readonly<{
  requireUser?: boolean;
  requiresResume?: boolean;
}>;

export type ButtonConfig = Readonly<{
  buttonTitle: string;
  disabledInProd: boolean;
  requirements?: ButtonRequirements;
  onClick?: () => void;
  actionButtonText?: string;
}>;

export type MappedButtonConfigs = Map<AskLubavButtonEndpoint, ButtonConfig>;

export type AskLubavButtonGroupProps = Readonly<{
  isDialogOpen: boolean;
  handleApiCall: (
    title: AskLubavButtonEndpoint,
  ) => Promise<HandleAPICallResponseReturn | undefined>;
  tier?: Tier;
  /** if in dev or staging, we can just have all options enabled */
  enableAllOptions?: boolean;
  user: BackrsUser | undefined;
  buttonConfigs: MappedButtonConfigs;
  askLubavButtonResponse?: string;
  AskLubavErrorDisplay?: React.ComponentType<{
    errorCode: AskLubavErrorCode | undefined;
  }>;
}>;

export const AskLubavButtonGroup = ({
  isDialogOpen,
  handleApiCall,
  tier,
  enableAllOptions = false,
  buttonConfigs,
  askLubavButtonResponse,
  user,
  AskLubavErrorDisplay,
}: AskLubavButtonGroupProps): ReactElement => {
  const [selectedButton, setSelectedButton] = useState<AskLubavButtonEndpoint | null>(null);
  const [loadingButton, setLoadingButton] = useState<AskLubavButtonEndpoint>();
  const [errorCode, setErrorCode] = useState<AskLubavErrorCode>();

  useEffect(() => {
    if (!isDialogOpen) {
      // on dialog close, reset the selected button
      setSelectedButton(null);
      setLoadingButton(undefined);
      setErrorCode(undefined);
    }
  }, [isDialogOpen]);

  const handleApiCallWrapper = async (key: AskLubavButtonEndpoint) => {
    setErrorCode(undefined);
    setLoadingButton(key);

    const result = await handleApiCall?.(key);

    if (result) {
      setErrorCode(result.errorCode);
      setLoadingButton(undefined);

      return;
    }

    setLoadingButton(undefined);
  };

  return (
    <Box sx={containerStyle} id="career-path-dialog-container-lubav">
      <Box sx={gridStyle}>
        <BackrsIcon
          name="LubavBubble"
          alt="Coach Lubav"
          height={40}
          width={40}
          style={lubavBubbleStyle}
        />
        <Box sx={buttonContainerStyle}>
          {!!handleApiCall &&
            (buttonConfigs ? Array.from(buttonConfigs.entries()) : []).map(([key, config]) => (
              <AskLubavButton
                key={key}
                apiKey={key}
                config={config}
                isSelected={selectedButton === key}
                setIsSelected={() => setSelectedButton(selectedButton === key ? null : key)}
                onButtonClick={handleApiCallWrapper}
                isLoading={loadingButton === key}
                isAnyLoading={!!loadingButton}
                user={user}
                enableAllOptions={enableAllOptions}
              />
            ))}
        </Box>
      </Box>
      {askLubavButtonResponse && selectedButton ? (
        <AskLubavResponse
          response={askLubavButtonResponse}
          tier={tier}
          apiKeyAction={{
            key: selectedButton,
            onClick: buttonConfigs?.get(selectedButton)?.onClick || undefined,
            actionButtonText: buttonConfigs?.get(selectedButton)?.actionButtonText || undefined,
          }}
        />
      ) : null}
      {errorCode && AskLubavErrorDisplay && <AskLubavErrorDisplay errorCode={errorCode} />}
    </Box>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  marginTop: '1rem',
  marginBottom: '1rem',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: '16px',
  rowGap: '8px',
};

const lubavBubbleStyle: React.CSSProperties = {
  borderRadius: '50%',
};

const gridStyle: SxProps<Theme> = {
  display: {
    xs: 'flex',
    md: 'grid',
  },
  gridTemplateColumns: '40px 1fr',
  gap: '16px',
};
